import React, { useContext, useRef, useEffect, useState } from 'react'
import { Card, CardBody } from 'reactstrap'
import { IconX, IconCheck, IconCopy } from '@tabler/icons-react'
import ChatMessageContent from '../chat-history/ChatMessageContent'
import { ChatbotContext } from '../context/ChatbotProvider'
import { staticAssetsPath } from '../../../../configs/constants'
import { useSasToken } from '../../../../_hooks/useSasToken'
import { Tooltip } from 'react-tooltip'
import { useSelector } from 'react-redux'
import { getModelDisplayName } from '../utils/constants'

interface ComparisonWindowProps {
  isHistoryOpened?: boolean
}

const ComparisonWindow: React.FC<ComparisonWindowProps> = ({ isHistoryOpened = false }) => {
  const { messages } = useContext(ChatbotContext || [])
  const { chatModels } = useSelector((state: any) => state.chatbotReducer)
  const { selectedMessage, setSelectedMessage } = useContext(ChatbotContext)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [opening, setOpening] = useState(false)
  const [closing, setClosing] = useState(false)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const { appendToken } = useSasToken()
  const imgPath = appendToken(`${staticAssetsPath}/images/icons/ali_icon.svg`)
  const selectedMsg: any = messages.find((msg: any) => msg.parentId === selectedMessage?.parentId)
  const width = window.innerWidth

  // Determine screen size
  let screenSize: 'small' | 'wide' | 'default' = 'default'
  if (width <= 1650) {
    screenSize = 'small'
  } else if (width >= 2250) {
    screenSize = 'wide'
  }

  useEffect(() => {
    setTimeout(() => {
      setOpening(selectedMessage)
    }, 300)
  }, [selectedMessage])

  const handleClose = () => {
    setClosing(true)
    setTimeout(() => {
      setSelectedMessage(null)
    }, 300) // Match this duration with your CSS transition duration
  }

  useEffect(() => {
    setTimeout(() => {
      if (scrollContainerRef.current && selectedMessage && opening && selectedMsg) {
        const selectedCard = document.querySelector(
          `[selected-message-index="${selectedMsg.activeResponseIndex}"]`
        )
        if (selectedCard) {
          selectedCard.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest'
          })
        }
      }
    }, 250)
  }, [selectedMessage, opening, selectedMsg])

  const onCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text)
    setCopiedIndex(index)

    setTimeout(() => {
      setCopiedIndex(null)
    }, 3000)
  }

  return (
    <div
      data-testid="comparison-window"
      className={`flex-grow px-50 citations overflow-y-hidden max-h-full theme-frosted-panel-muted transition-all duration-300 ease-in-out transform translate-x-full 
        ${isHistoryOpened ? 'max-w-[55%]' : 'max-w-[65%]'} 
        ${opening ? 'transform-none opacity-100' : ''} 
        ${closing ? 'translate-x-full opacity-0 closing' : ''}`}>
      <div className="d-flex justify-content-between align-items-center">
        <h4 className="d-flex m-1 theme-text-heading">
          <b>Response Comparison</b>
        </h4>
        <IconX
          onClick={handleClose}
          size={28}
          data-testid="close-button"
          className="!p-1 theme-icon-muted cursor-pointer !mr-2 !bg-transparent theme-hover-surface transition-colors"
        />
      </div>
      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto overflow-y-hidden h-[calc(100%-60px)] p-2.5 scrollbar-thin scrollbar-thumb-[var(--theme-border)] scrollbar-track-transparent hover:scrollbar-thumb-[var(--text-subheading)]">
        {selectedMsg?.response?.map((msg: any, index: number) => (
          <Card
            key={index}
            selected-message-index={index}
            className={`mx-[5px] pb-0 flex flex-col h-full flex-1 overflow-hidden chat-primary theme-border-default [&.card]:!rounded-lg [&.card]:!mb-0
              ${
                selectedMsg?.activeResponseIndex === index
                  ? 'border theme-border-selected shadow-lg'
                  : 'border theme-border-default shadow-lg'
              } 
              ${
                screenSize === 'small'
                  ? '!min-w-[50%]'
                  : screenSize === 'wide'
                  ? '!min-w-[30%]'
                  : '!min-w-[40%]'
              }`}>
            <CardBody className="flex flex-col h-full">
              <div className="flex justify-between items-center p-[5px]">
                <div className="flex">
                  <img src={imgPath} alt="Ali" width={30} />
                  <b className="ms-1 theme-text-heading">
                    {getModelDisplayName(chatModels, msg?.modelVersion)}
                  </b>
                </div>
                {copiedIndex === index ? (
                  <span
                    className={`icon-default ml-1.5 text-green-500 [&>svg]:mr-1.5 chat-primary`}>
                    <IconCheck size={20} />
                  </span>
                ) : (
                  <>
                    <IconCopy
                      size={18}
                      className="cursor-pointer theme-icon-muted hover:text-[var(--color-primary)] transition-colors"
                      onClick={() => onCopy(msg?.content, index)}
                      data-testid={`copy-button-${index}`}
                    />
                    <Tooltip
                      id="copy-chat"
                      className="!rounded-md !p-2.5 !max-w-[250px] !text-center !z-[9999]">
                      Copy
                    </Tooltip>
                  </>
                )}
              </div>
              <hr className="theme-border-default" />
              <span className="flex-grow overflow-y-auto mt-1">
                <ChatMessageContent
                  message={selectedMsg}
                  responseIndex={index}
                  isPromptEditing={false}
                />
              </span>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default ComparisonWindow
